import { css, type Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { Box, Flex, Text } from '@toolbox/components';
import { useMarketTrends } from '@widget/core';
import { USPItem } from '../USPItem';
import { useTheme } from '@widget/toolbox/style/ThemeProvider';
import { CloseIcon } from '@toolbox/components/Icon/icons/CloseIcon';
import { makeStyles } from '@widget/toolbox/style/classes/makeStyles';

import type { USPItemProps } from '../USPItem';

type LocalizedUspListProps = {
  setIsClosed: (isClosed: boolean) => void;
};

export const LabelContainer = styled(Flex)`
  font-size: 12px;
  ${({ theme }) => theme.media.up('md')} {
    font-size: 14px;
  }
`;

export const StyledBox = styled(Box)`
  max-width: 100vw;
`;

const useStyles = makeStyles(({ theme }) => ({
  uspContainer: css`
    width: 100%;
    background-color: ${theme.palette.colors.primary};
    color: ${theme.palette.colors.white};
    padding: 12px 18px;
    margin: 1rem 0;
    margin-right: auto;
    display: flex;
    align-items: flex-start;
    column-gap: 16px;
    span {
      display: inline-block;
    }
    p {
      font-size: 13px;
      margin: 0;
    }
  `,
  uspContainerMini: css`
    margin: 0;
  `,
  uspMainPart: css`
    display: flex;
    ${theme.media.down('sm')} {
      flex-direction: column;
      width: 100%;
    }
    ${theme.media.up('sm')} {
      flex-direction: row;
      width: 100%;
      flex: 1;
      align-items: center;
    }
  `,
  message: css`
    color: ${theme.palette.colors.white};

    p {
      color: ${theme.palette.colors.white};
    }
  `,
  textStyle: (theme: Theme) => css`
    align-items: center;
    padding: 0;
    line-height: 24px;
    font-size: 16px;
    font-weight: 700;
  `,
  closeStyle: (theme: Theme) => css`
    flex: 0 0 12px;
    height: 23px;
    cursor: pointer;
    margin-left: auto;
    ${theme.media.down('sm')} {
      height: 12px;
      margin-top: 6px;
    }
  `,
  listStyle: (theme: Theme) => css`
    display: flex;
    line-height: 24px;
    font-size: 16px;
    font-weight: 500;
    ${theme.media.down('sm')} {
      flex-direction: column;
      padding-left: 22px;
      list-style-type: disc;
    }
    ${theme.media.up('sm')} {
      flex-direction: row;
      justify-content: space-between;
      flex: 1;
      padding: 0 4rem;
      list-style: none;
    }
    color: ${theme.palette.colors.white};
    padding-left: 1rem;
  `,
}));
export const LocalizedUspList = ({ setIsClosed }: LocalizedUspListProps): JSX.Element | null => {
  const { marketTrends } = useMarketTrends();
  const theme = useTheme();
  const classes = useStyles();

  if (!marketTrends) {
    return <></>;
  }

  const upsItemsList: USPItemProps[] = [
    {
      label: `⌀ ${marketTrends.averageSqmPrice} Euro/m²`,
      icon: 'avgSMPricePerRegionIcon',
      iconBig: true,
    },
    {
      label: `⌀ ${marketTrends.averageSellingTime} Tage bis zum Verkauf`,
      icon: 'sellingHorizon',
      iconBig: true,
    },
    {
      label: `${marketTrends.brokerCount} Makler vor Ort`,
      icon: 'nrofBrokersPerRegion',
      iconBig: true,
    },
  ];

  return (
    <Box css={[classes.uspContainer, classes.uspContainerMini] as any[]}>
      <Box css={[classes.uspMainPart] as any[]}>
        <Text color={theme.palette.colors.white} css={classes.textStyle as any}>
          In Ihrer Region
        </Text>
        <ul css={classes.listStyle as any}>
          {upsItemsList?.map((props, index) => (
            <li key={index}>
              <USPItem {...props} />
            </li>
          ))}
        </ul>
      </Box>
      <CloseIcon onClick={() => setIsClosed(true)} css={classes.closeStyle as any} />
    </Box>
  );
};
